import api from '@/libs/api'
import { isTruthy, hasProperty } from '@/libs/helpers'
import { OFFLINE, PAUSED } from '@/constants/agentStatuses'
import { ROUTE_ATTENDANCE_NEW } from '@/router/constants'
import moment from 'moment-timezone'
import { isPauseLocked } from '@/libs/systemPause'

const router = document.router
const threeSeconds = 3000
const START_ATTENDANCE = 'START_ATTENDANCE'
const END_ATTENDANCE = 'END_ATTENDANCE'
const UPDATE_AGENT = 'UPDATE_AGENT'
const UPDATE_SESSION = 'UPDATE_SESSION'
const SET_SESSION = 'SET_SESSION'
const CLEAR_SESSION = 'CLEAR_SESSION'
const SET_PROVISION = 'SET_PROVISION'
const SET_AUTO_REJECTION = 'SET_AUTO_REJECTION'
const SET_SCHEDULE_PAUSE = 'SET_SCHEDULE_PAUSE'
const SET_SCHEDULE_LOGOUT = 'SET_SCHEDULE_LOGOUT'
const SET_JOURNEY_PRE_PAUSE = 'SET_JOURNEY_PRE_PAUSE'
const UPDATE_SESSION_LOADING = 'UPDATE_SESSION_LOADING'
const SET_QUEUE_PROPERTIES = 'SET_QUEUE_PROPERTIES'

const RELOAD_SESSION = 'RELOAD_SESSION'
const START_RELOAD_SESSION = 'START_RELOAD_SESSION'
const STOP_RELOAD_SESSION = 'STOP_RELOAD_SESSION'

let loadingSession = false
let offlineTimeout = null
let awaitingOnline = false

const actions = {
  setAutoRejection ({ commit, state }, active) {
    if (state.journeyPrePause && !active) return
    commit(SET_AUTO_REJECTION, active)
  },

  setJourneyPrePause ({ commit }, payload) {
    commit(SET_JOURNEY_PRE_PAUSE, payload)
  },

  clearJourneyPrePause ({ commit }) {
    commit(SET_JOURNEY_PRE_PAUSE, null)
  },

  setSchedulePause ({ commit }, reasonId) {
    commit(SET_SCHEDULE_PAUSE, reasonId)
  },

  setScheduleLogout ({ commit }) {
    commit(SET_SCHEDULE_LOGOUT, true)
  },

  clearSchedulePause ({ commit }) {
    commit(SET_SCHEDULE_PAUSE, null)
  },

  clearScheduleLogout ({ commit }) {
    commit(SET_SCHEDULE_LOGOUT, false)
  },

  setConnectSip (ctx, obj) {
    ctx.commit(UPDATE_SESSION, { loading: true, retryTimeout: 0, loadType: 'sip', ...obj })
  },

  setProvision ({ commit }, provision) {
    commit(SET_PROVISION, provision)
  },

  clearSession ({ commit }) {
    commit(CLEAR_SESSION)
  },

  async getSession (ctx, agent) {
    if (loadingSession) return
    loadingSession = true

    try {
      ctx.commit(UPDATE_SESSION_LOADING, { loading: true })
      const session = await getSession(agent.id, true)
      loadingSession = false
      ctx.commit(SET_SESSION, session)
    } catch {
      loadingSession = false
      ctx.commit(UPDATE_SESSION_LOADING, { loading: false })
    }
  },

  async getQueuesProperties (ctx, queueList) {
    if (!queueList) return

    const queuesProperties = await api.getQueuesProperties(queueList)
    ctx.commit(SET_QUEUE_PROPERTIES, queuesProperties)
  },

  async reloadSession (ctx) {
    ctx.commit(START_RELOAD_SESSION)
    const agent = ctx.state?.session?.agent
    if (!agent?.id) return

    if (loadingSession) return
    loadingSession = true

    try {
      const session = await getSession(agent.id)
      loadingSession = false
      ctx.commit(RELOAD_SESSION, session)
    } catch {
      loadingSession = false
      ctx.commit(STOP_RELOAD_SESSION)
    }
  },

  updateAgent (ctx, agentData) {
    const oldAgentState = ctx?.state?.session?.agent ?? {}
    const newAgentState = { ...oldAgentState, ...(agentData ?? {}) }
    console.log({ agentData, oldAgentState, newAgentState })
    if (isStatusOffline(agentData.status)) agentData.status = OFFLINE
    if (isStatusOffline(oldAgentState.status)) oldAgentState.status = OFFLINE

    if (!newAgentState.summary) newAgentState.summary = { pauseSecs: 0 }

    if (newAgentState.status === PAUSED) {
      newAgentState.pauseAt = Date.now()
      ctx.commit(START_ATTENDANCE)
      ctx.commit(SET_AUTO_REJECTION, false)
      ctx.commit(SET_JOURNEY_PRE_PAUSE, null)
    }

    newAgentState.statusChanged = oldAgentState.status !== agentData.status
    ctx.commit(UPDATE_AGENT, newAgentState)
  },

  loadingSession (ctx) {
    ctx.commit(UPDATE_SESSION_LOADING, { loading: true })
  },

  startAttendance () {},

  endAttendance (ctx) {
    ctx.commit(END_ATTENDANCE)
  }
}

const setSession = (state, newSession) => {
  const templates = Array.isArray(newSession.templates) ? newSession.templates : []
  const pauseReasons = Array.isArray(newSession.pauseReasons) ? newSession.pauseReasons : []
  const entryNumbers = []
  const emailConfigs = Array.isArray(newSession.emailConfigs) ? newSession.emailConfigs : []
  const availableAgents = entryNumbers.filter(byAgentType)
  const allEmailConfigs = Array.isArray(newSession.allEmailConfigs) ? newSession.allEmailConfigs : []

  const session = state.session ? { ...state.session } : {}
  session.loading = false
  session.reloading = false
  if (!session.agent) session.agent = {}

  const { status, active, ...sessionAgent } = newSession.agent
  if (sessionAgent.pauseAt) sessionAgent.pauseAt = sessionAgent.pauseAt * 1000

  const agent = { ...session.agent, ...sessionAgent }
  agent.paused = agent.status === PAUSED
  agent.logged = agent.status && agent.status !== OFFLINE

  const configs = Object.assign(structuredClone(session.configs), newSession.configs)

  Object.assign(session, {
    agent,
    templates,
    entryNumbers,
    emailConfigs,
    pauseReasons,
    availableAgents,
    allEmailConfigs,
    configs
  })

  state.session = session
}

const mutations = {
  [UPDATE_SESSION_LOADING]: (state, load) => {
    const session = state.session ? { ...state.session } : {}
    delete session.loadType
    delete session.loading
    delete session.retryCount
    delete session.retryTimeout
    state.session = Object.assign(session, load)
  },

  [START_RELOAD_SESSION]: (state) => {
    state.session.reloading = true
  },

  [STOP_RELOAD_SESSION]: (state) => {
    state.session.reloading = false
  },

  [SET_QUEUE_PROPERTIES]: (state, queuesProperties) => {
    state.queuesProperties = { ...state.queuesProperties, ...queuesProperties }
  },

  [SET_SESSION]: setSession,
  [RELOAD_SESSION]: setSession,

  [UPDATE_SESSION]: (state, newSession) => {
    const session = Object.assign({}, state.session ?? {}, newSession ?? {})

    if (session.agent?.id) {
      session.agent = { ...session.agent }
      session.agent.id = Number(session.agent.id)
    }

    state.session = session
  },

  [SET_PROVISION]: (state, provision) => {
    state.provision = provision
  },

  [CLEAR_SESSION]: (state) => {
    const session = newSession()
    session.loading = false
    state.session = session
  },

  [SET_AUTO_REJECTION]: (state, active) => {
    state.autoRejection = Boolean(active)
    state.session.schedulePause = null
    state.session.scheduleLogout = false
  },

  [SET_JOURNEY_PRE_PAUSE]: (state, payload) => {
    state.journeyPrePause = payload
    state.autoRejection = Boolean(payload)
    if (payload) {
      state.session.schedulePause = null
      state.session.scheduleLogout = false
    }
  },

  [SET_SCHEDULE_PAUSE]: (state, reasonId) => {
    state.autoRejection = false
    state.session.schedulePause = reasonId
    if (reasonId !== null) state.session.scheduleLogout = false
  },

  [SET_SCHEDULE_LOGOUT]: (state, hasSchedule) => {
    state.autoRejection = false
    state.session.scheduleLogout = hasSchedule
    if (hasSchedule) state.session.schedulePause = null
  },

  [UPDATE_AGENT]: (state, agent) => {
    const session = { ...(state.session ?? {}) }
    if (!session.agent) session.agent = {}
    session.agent = { ...session.agent, ...agent }
    if (session.agent?.id) session.agent.id = Number(session.agent.id)
    state.session = session
  },

  [START_ATTENDANCE]: (state) => {
    const agent = { ...state.session.agent }

    if (!agent.attendancesCount) agent.attendancesCount = 0
    agent.attendancesCount += 1

    if (!agent.inAttendance) {
      agent.inAttendance = true
      agent.inAttendanceAt = Date.now()
    }

    state.session = { ...state.session, agent }
  },

  [END_ATTENDANCE]: (state) => {
    const agent = { ...state.session.agent }
    if (agent.attendancesCount) agent.attendancesCount -= 1

    if (!agent.attendancesCount) {
      const callSecs = updateTimeElapsed(agent.summary.callSecs, agent.inAttendanceAt)
      agent.inAttendance = false
      agent.inAttendanceAt = null
      agent.summary = { ...agent.summary, callSecs }
    }

    state.session = { ...state.session, agent }
  }
}

const clearLogoutState = () => {
  offlineTimeout = null
  awaitingOnline = false
}

const cancelScheduledLogoutCognito = () => {
  clearTimeout(offlineTimeout)
  clearLogoutState()
}

const scheduleLogoutCognito = (dispatch) => {
  awaitingOnline = true

  if (offlineTimeout) return

  offlineTimeout = setTimeout(() => {
    if (awaitingOnline) dispatch('authModule/logout', null, { root: true })
    clearLogoutState()
  }, 500)
}

const getSession = (agentId, isLogin, retry = 9) => {
  return api.getSession(agentId, isLogin).catch((err) => {
    console.log(err)
    if (retry <= 0) return null
    return wait(threeSeconds).then(() => getSession(agentId, isLogin, retry - 1))
  })
}

const getters = {
  agent: ({ session }) => session.agent.id,
  configs: ({ session }) => session?.configs ?? {},

  flag: (state, { configs }) => (name) => (
    hasProperty(configs, name) ? isTruthy(configs[name]) : undefined
  ),

  areaCode: ({ provision }) => provision.areaCode,
  getAgent: ({ session }) => session.agent,
  isPauseLocked: ({ session }) => isPauseLocked(session.agent),
  getAllEmailConfigs: ({ session }) => session.allEmailConfigs,
  getEmailConfigs: ({ session }) => session.emailConfigs,
  getTemplates: ({ session }) => session.templates,
  hasAutoRejection: ({ autoRejection }) => Boolean(autoRejection),
  hasJourneyPrePause: ({ journeyPrePause }) => Boolean(journeyPrePause),
  journeyPrePause: ({ journeyPrePause }) => journeyPrePause,
  hasScheduleLogout: ({ session }) => session.scheduleLogout,
  hasSchedulePause: ({ session }) => session.schedulePause,
  inAttendanceAt: ({ session }) => session.agent.inAttendanceAt,
  provision: ({ provision }) => provision,
  session: ({ session }) => session,
  sesssionReloading: ({ session }) => session.reloading,
  sessionMembership: ({ session }) => session.agent.membership,
  queueProperties: (state) => (queueId) => {
    const props = state.queuesProperties || state.session?.queuesProperties || {}
    return props[queueId] || {}
  },
  token: ({ provision }) => provision?.authorization?.token
}

const newSession = () => ({
  agent: {
    summary: {},
    membership: [],
    inAttendance: false,
    inAttendanceAt: null
  },

  templates: [],
  pauseReasons: [],
  entryNumbers: [],
  availableAgents: [],
  emailConfigs: [],
  allEmailConfigs: [],
  queuesProperties: {},
  configs: {},

  loading: true,

  schedulePause: null,
  scheduleLogout: false
})

const middlewares = {
  'agentModule/SET_PROVISION': (ctx) => {
    ctx.dispatch('sipModule/connect', null, { root: true })
  },

  'agentModule/UPDATE_AGENT': ({ dispatch, state }, { payload: agent }) => {
    const isOfflineAgent = agent?.status === OFFLINE
    const isOnline = !isOfflineAgent

    if (isOnline && awaitingOnline) {
      cancelScheduledLogoutCognito()
    }

    if (isOfflineAgent) {
      return scheduleLogoutCognito(dispatch)
    }

    const hasQueuesProperties = state.agentModule?.queuesProperties

    if (agent.queueList && !hasQueuesProperties) {
      const queueList = agent.queueList.split(',').filter(q => !q.startsWith('iqda')).join(',')
      dispatch('agentModule/getQueuesProperties', queueList, { root: true })
    }

    if (agent.statusChanged) {
      dispatch('agentModule/getSession', agent, { root: true })
      dispatch('attendanceModule/setHistory', agent, { root: true })
      return router.push({ name: ROUTE_ATTENDANCE_NEW }).catch(() => null)
    }
  },

  'agentModule/SET_SESSION': ({ dispatch }, { payload: session }) => {
    const agent = session.agent || {}

    if (agent.id) {
      const date = moment().format('YYYY-MM-DD HH:mm:ss')
      dispatch('calendarModule/getAppointments', { agentId: agent.id, date })
    }
  },

  'attendanceModule/FINISH_ATTENDANCE_VOICE': ({ state, dispatch }) => {
    if (!state?.agentModule?.session?.agent?.inAttendanceAt) return
    dispatch('agentModule/endAttendance', null, { root: true })
  },

  'sipModule/SIP_CONNECTED': ({ state, dispatch }, { payload: sipWasConnected }) => {
    if (!sipWasConnected) return
    const agentId = state?.agentModule?.session?.agent?.id
    if (agentId) return dispatch('agentModule/getSession', { id: agentId }, { root: true })
  }
}

export default {
  namespaced: true,

  getters,
  actions,
  mutations,
  middlewares,

  state: {
    provision: {},
    session: newSession(),
    autoRejection: false,
    journeyPrePause: null
  }
}

const byAgentType = (agent) => (agent.destinationType === 'AGENTCODE')
const wait = (ms) => (new Promise((resolve) => setTimeout(resolve, ms)))
const isStatusOffline = (status) => ([null, undefined, '', OFFLINE].includes(status))
const updateTimeElapsed = (previous, initAt) => ((previous || 0) + Math.floor((Date.now() - initAt) / 1000))
